/**
 * §5.7 — an entry's recorded changes, as sentences.
 *
 * WHERE THE RECORD COMES FROM
 *
 * Migration 0027's triggers write every change to a posted entry into
 * audit_log, and GET /journals/:id/history groups the rows one save produced
 * into one change. The payloads are what the triggers could see: whole rows
 * as `to_jsonb`, so column names are snake_case and ids are ids. This turns
 * them into what the operator changed.
 *
 * A SEPARATE FILE for the reason `money.ts` and `labels.ts` are separate: the
 * editor is already long, and a formatter with a case per action is easier to
 * test directly than through a rendered screen.
 *
 * ORDER WITHIN ONE CHANGE IS NOT MEANINGFUL. The events of one save share a
 * timestamp and their ids sort randomly inside a millisecond (see the API's
 * entry-history service). So this sorts nothing by time and states nothing as
 * "then": lines are listed by line number, which is stable and means something.
 *
 * AN UNKNOWN ACTION STILL RENDERS. A later migration may record something new,
 * and a history that silently drops what it does not recognise is a history
 * that lies by omission.
 */

import type { EntryChange, EntryHistoryEvent } from '../api.js';
import { formatCents } from './money.js';
import { formatTimestamp } from './timezone.js';

export interface HistoryLookups {
  /** "6100 Office Supplies" for an account id, or null when it is not known. */
  accountLabel: (accountId: string) => string | null;
  /** A party's name for its id, or null when it is not known. */
  partyName: (partyId: string) => string | null;
}

type Row = Record<string, unknown>;

/** The header columns an operator can change, in their words. */
const FIELD_LABELS: Record<string, string> = {
  entry_date: 'Entry Date',
  cash_date: 'Cash Date',
  memo: 'Memo',
  party_id: 'Party',
  invoice_reference: 'Invoice Number',
  counterparty: 'Counterparty',
};

function fieldLabel(key: string): string {
  return (
    FIELD_LABELS[key] ??
    key
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  );
}

function text(value: unknown): string {
  if (value === null || value === undefined || value === '') return 'blank';
  return String(value);
}

function describeLine(row: unknown, lookups: HistoryLookups): string {
  const line = (row ?? {}) as Row;
  const accountId = typeof line.account_id === 'string' ? line.account_id : '';
  const account = lookups.accountLabel(accountId) ?? 'an account no longer listed';
  const debit = Number(line.debit_cents ?? 0);
  const credit = Number(line.credit_cents ?? 0);
  return debit > 0
    ? `${account}, debit ${formatCents(debit)}`
    : `${account}, credit ${formatCents(credit)}`;
}

function lineNumber(row: unknown): number {
  return Number(((row ?? {}) as Row).line_number ?? 0);
}

/** One event as one or more sentences. A header change names each field. */
export function describeEvent(event: EntryHistoryEvent, lookups: HistoryLookups): string[] {
  const payload = event.payload;

  switch (event.action) {
    case 'posted':
      return ['Posted'];
    case 'reversed':
      return ['Reversed'];
    case 'entry_deleted':
      return ['Deleted the entry'];
    case 'receipt_removed':
      return [`Removed receipt ${text(payload.originalFilename)}`];
    case 'line_added':
      return [`Added line ${lineNumber(payload.after)}: ${describeLine(payload.after, lookups)}`];
    case 'line_removed':
      return [
        `Removed line ${lineNumber(payload.before)}: ${describeLine(payload.before, lookups)}`,
      ];
    case 'line_changed':
      return [
        `Changed line ${lineNumber(payload.after)} from ${describeLine(payload.before, lookups)} to ${describeLine(payload.after, lookups)}`,
      ];
    case 'header_changed': {
      const changes = (payload.changes ?? {}) as Record<string, { from?: unknown; to?: unknown }>;
      return Object.keys(changes)
        .sort()
        .map((key) => {
          const { from, to } = changes[key] ?? {};
          const show = (value: unknown) =>
            key === 'party_id' && typeof value === 'string'
              ? (lookups.partyName(value) ?? 'a party no longer listed')
              : text(value);
          return `${fieldLabel(key)} changed from ${show(from)} to ${show(to)}`;
        });
    }
    default:
      return [event.action.replace(/_/g, ' ')];
  }
}

/**
 * Every sentence for one change.
 *
 * Header fields first, then lines by line number, then everything else. That
 * is a reading order, not a claim about which happened first.
 */
export function describeChange(change: EntryChange, lookups: HistoryLookups): string[] {
  const rank = (event: EntryHistoryEvent): number => {
    if (event.action === 'header_changed') return 0;
    if (event.action.startsWith('line_')) return 1;
    return 2;
  };
  const number = (event: EntryHistoryEvent): number =>
    lineNumber(event.payload.after ?? event.payload.before);

  return [...change.events]
    .sort((a, b) => rank(a) - rank(b) || number(a) - number(b))
    .flatMap((event) => describeEvent(event, lookups));
}

/**
 * When, in the zone the operator chose on the Settings screen.
 *
 * It used to print the server's own offset — "2026-09-13 14:02 UTC" — which is
 * unambiguous and is not what a person reading their own books wants to do
 * arithmetic on. `lib/timezone.ts` holds the preference and the formatting; the
 * careful parsing that used to live here moved there with it, because the
 * reason for it did not change: the server sends "2026-09-13 14:02:11.123+00",
 * which is not ISO 8601, and handing that to `new Date` is unspecified.
 *
 * Kept as a named export rather than replaced at each call site: the editor's
 * history is what it means, and "when" reads better there than the generic
 * name does.
 */
export function formatWhen(at: string): string {
  return formatTimestamp(at);
}
